import { getSession } from '../session.js';
import { showToast } from './Toast.js';
import type { DocumentTemplate } from '../../core/app-store.js';
import { getPreviewTemplates } from '../template-cache.js';

type NavigateFn = (route: string, params?: Record<string, string>) => void;

export async function renderTemplateLibrary(container: HTMLElement, navigate: NavigateFn): Promise<void> {
  const session = getSession();
  const industry = session?.industry ?? 'general';

  container.innerHTML = `
    <div class="page-header">
      <div style="display:flex;justify-content:space-between;align-items:flex-start">
        <div>
          <h1 class="page-title">Template Library</h1>
          <p class="page-subtitle">Pre-built and custom document templates for your industry</p>
        </div>
        <button class="btn btn-gold" id="new-template-btn">+ Build Template</button>
      </div>
    </div>

    <div style="display:flex;gap:8px;margin-bottom:20px;flex-wrap:wrap" id="filter-bar">
      <button class="filter-chip active" data-filter="all">All</button>
      ${industry === 'construction' ? `
        <button class="filter-chip" data-filter="Contracts">Contracts</button>
        <button class="filter-chip" data-filter="Change Orders">Change Orders</button>
        <button class="filter-chip" data-filter="RFIs">RFIs</button>
        <button class="filter-chip" data-filter="Field Reports">Field Reports</button>
        <button class="filter-chip" data-filter="Legal">Legal</button>
      ` : industry === 'restaurant' ? `
        <button class="filter-chip" data-filter="Contracts">Contracts</button>
        <button class="filter-chip" data-filter="HR">HR</button>
        <button class="filter-chip" data-filter="Legal">Legal</button>
      ` : `
        <button class="filter-chip" data-filter="Legal">Legal</button>
        <button class="filter-chip" data-filter="Contracts">Contracts</button>
      `}
      <button class="filter-chip" data-filter="custom">My Templates</button>
    </div>

    <div class="template-grid" id="template-grid">
      <div class="empty-state"><div class="spinner"></div></div>
    </div>
  `;

  document.getElementById('new-template-btn')!.addEventListener('click', () => navigate('template-builder'));

  document.getElementById('filter-bar')!.querySelectorAll('.filter-chip').forEach((btn) => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.filter-chip').forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');
      filterTemplates(btn.getAttribute('data-filter') ?? 'all');
    });
  });

  await loadTemplates(industry, navigate);
}

let allTemplates: DocumentTemplate[] = [];

async function loadTemplates(industry: string, navigate: NavigateFn): Promise<void> {
  const grid = document.getElementById('template-grid')!;
  const session = getSession();

  try {
    const api = getAPI();
    allTemplates = await api.getTemplates(session?.businessId, industry) as DocumentTemplate[];
  } catch {
    allTemplates = getPreviewTemplates(industry);
  }

  renderGrid(allTemplates, navigate);
}

function filterTemplates(filter: string): void {
  if (filter === 'all') { renderGrid(allTemplates, currentNavigate!); return; }
  if (filter === 'custom') {
    renderGrid(allTemplates.filter((t) => !t.isPreset), currentNavigate!);
    return;
  }
  renderGrid(allTemplates.filter((t) => t.category === filter), currentNavigate!);
}

let currentNavigate: NavigateFn | null = null;

function renderGrid(templates: DocumentTemplate[], navigate: NavigateFn): void {
  currentNavigate = navigate;
  const grid = document.getElementById('template-grid')!;

  if (templates.length === 0) {
    grid.innerHTML = `
      <div class="empty-state" style="grid-column:1/-1">
        <div class="empty-state-icon">📄</div>
        <div class="empty-state-title">No Templates Found</div>
        <div class="empty-state-desc">Create your first custom template or switch the filter above.</div>
      </div>
    `;
    return;
  }

  const categoryColors: Record<string, string> = {
    'Contracts': '#2563eb',
    'Change Orders': '#d97706',
    'RFIs': '#7c3aed',
    'Field Reports': '#059669',
    'Legal': '#dc2626',
    'HR': '#0891b2',
  };

  grid.innerHTML = templates.map((t) => `
    <div class="template-card" data-id="${t.id}">
      <div class="template-card-cat" style="background:${categoryColors[t.category] ?? 'var(--brand-blue)'}">
        ${t.category}${t.isPreset ? ' · PRESET' : ''}
      </div>
      <div class="template-card-name">${escHtml(t.name)}</div>
      <div class="template-card-desc">${escHtml(t.description)}</div>
      <div class="template-card-meta">${t.fields.length} fields · Used ${t.useCount}×</div>
      <div class="template-card-actions">
        <button class="btn btn-gold btn-sm" data-action="use" data-id="${t.id}">Use Template →</button>
        ${!t.isPreset ? `<button class="btn btn-ghost btn-sm" data-action="edit" data-id="${t.id}">Edit</button>` : ''}
      </div>
    </div>
  `).join('');

  grid.querySelectorAll('[data-action]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const action = btn.getAttribute('data-action')!;
      const id = btn.getAttribute('data-id')!;
      if (action === 'use') navigate('fill-document', { templateId: id });
      if (action === 'edit') navigate('template-builder', { templateId: id });
    });
  });
}


function getAPI() {
  return (window as unknown as { preuve: Record<string, (...args: unknown[]) => Promise<unknown>> }).preuve;
}

function escHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
